import { Component, onMounted, onWillUnmount, useRef, useState } from "@odoo/owl";
import * as SourceMap from "source-map";

import UglifyJS from "./uglify-js";
import generateHtml from "./generateHtml";

const exampleKinds = ["coffee", "simple-coffee", "typescript", "babel", "sass"];
const SOURCE_MAPPING_URL_REG_EXP = /\/\/[@#]\s*sourceMappingURL\s*=\s*data:[^\n]*?base64,([^\n]*)/;
const SOURCE_MAPPING_URL_REG_EXP2 = /\/\*\s*[@#]\s*sourceMappingURL\s*=\s*data:[^\n]*?base64,([^\n]*)\s*\*\//;

const examples = {
	coffee: {
		jsUrl: "example/coffee/example.js",
		mapUrl: "example/coffee/example.map",
		originalUrl: "example/coffee/example",
	},
	"simple-coffee": {
		jsUrl: "example/simple-coffee/example.js",
		mapUrl: "example/simple-coffee/example.map",
		originalUrl: "example/simple-coffee/example",
	},
	typescript: {
		jsUrl: "example/typescript/example.js",
		mapUrl: "example/typescript/example.map",
		originalUrl: "example/typescript/example",
	},
	babel: {
		jsUrl: "example/babel/example.js",
		mapUrl: "example/babel/example.map",
		originalUrl: "example/babel/example",
	},
	sass: {
		jsUrl: "example/sass/example.js",
		mapUrl: "example/sass/example.map",
		originalUrl: "example/sass/example",
	},
};

const exampleCache = {};

const { SourceMapConsumer, SourceMapGenerator } = SourceMap;

function createInitialState() {
	return {
		visualization: null,
		currentExample: null,
		customLink: "",
		pageError: "",
		selectedMappingKey: "",
		currentHash: "",
		custom: {
			visible: false,
			step: "step1",
			error: "",
			hasPendingFile: false,
			generatedSource: "",
			sourceMap: null,
			sourcesContent: [],
			sourceFile: "",
			sourceFileIndex: -1,
		},
	};
}

function cloneMap(map) {
	return JSON.parse(JSON.stringify(map));
}

function replaceInlineSourceMap(generatedSource) {
	return generatedSource
		.replace(SOURCE_MAPPING_URL_REG_EXP, "/* base64 source map removed */")
		.replace(SOURCE_MAPPING_URL_REG_EXP2, "/* base64 source map removed */");
}

function extractInlineSourceMap(generatedSource) {
	if (typeof atob !== "function") return null;
	if (!SOURCE_MAPPING_URL_REG_EXP.test(generatedSource) && !SOURCE_MAPPING_URL_REG_EXP2.test(generatedSource)) {
		return null;
	}
	const match = SOURCE_MAPPING_URL_REG_EXP.exec(generatedSource) || SOURCE_MAPPING_URL_REG_EXP2.exec(generatedSource);
	if (!match) return null;
	try {
		return {
			generatedSource: replaceInlineSourceMap(generatedSource),
			sourceMap: JSON.parse(decodeURIComponent(escape(atob(match[1])))),
		};
	} catch (err) {
		return null;
	}
}

function readFile(file, callback) {
	const fileReader = new FileReader();
	fileReader.readAsText(file, "utf-8");
	fileReader.onload = function() {
		callback(null, fileReader.result);
	};
	fileReader.onabort = function() {
		callback(new Error("File read cancelled"));
	};
	fileReader.onerror = function(evt) {
		switch (evt.target.error.code) {
			case evt.target.error.NOT_FOUND_ERR:
				return callback(new Error("File Not Found!"));
			case evt.target.error.NOT_READABLE_ERR:
				return callback(new Error("File is not readable"));
			case evt.target.error.ABORT_ERR:
				return callback();
			default:
				return callback(new Error("An error occurred reading this file."));
		}
	};
}

function readFileAsText(file) {
	return new Promise((resolve, reject) => {
		readFile(file, (err, result) => {
			if (err) return reject(err);
			resolve(result);
		});
	});
}

function validateSourceMap(sourceMap) {
	if (!sourceMap || !sourceMap.sources) throw new Error("SourceMap has no sources field");
	if (!sourceMap.mappings) throw new Error("SourceMap has no mappings field");
}

function buildCustomHash(generatedSource, sourceMap, sourcesContent) {
	return (
		"#base64," +
		[generatedSource, JSON.stringify(sourceMap)]
			.concat(sourcesContent)
			.map((str) => btoa(unescape(encodeURIComponent(str))))
			.join(",")
	);
}

function getExample(kind) {
	return examples[kind] || examples.typescript;
}

function fetchText(url) {
	return fetch(url).then((response) => {
		if (!response.ok) {
			throw new Error(`Failed to load ${url}`);
		}
		return response.text();
	});
}

function fetchExample(kind) {
	if (!exampleCache[kind]) {
		const example = getExample(kind);
		exampleCache[kind] = Promise.all([fetchText(example.jsUrl), fetchText(example.mapUrl)])
			.then(([generatedSource, mapText]) => {
				const sourceMap = JSON.parse(mapText);
				if (sourceMap.sourcesContent) {
					return {
						js: generatedSource,
						map: sourceMap,
						original: null,
					};
				}
				return fetchText(example.originalUrl).then((originalSource) => ({
					js: generatedSource,
					map: sourceMap,
					original: originalSource,
				}));
			})
			.catch((err) => {
				delete exampleCache[kind];
				throw err;
			});
	}
	return exampleCache[kind];
}

class CodeBlock extends Component {
	getSegmentClass(segment) {
		let className = segment.className || "";
		if (segment.mappingKey && segment.mappingKey === this.props.selectedKey) {
			className += (className ? " " : "") + "selected";
		}
		return className;
	}

	onSegmentHover(ev) {
		const mappingKey = ev.currentTarget.dataset.mappingKey;
		if (mappingKey && this.props.onHover) {
			this.props.onHover(mappingKey);
		}
	}

	onSegmentClick(ev) {
		const mappingKey = ev.currentTarget.dataset.mappingKey;
		if (mappingKey && this.props.onActivate) {
			this.props.onActivate(ev, mappingKey);
		}
	}
}

CodeBlock.template = "source-map-visualization.CodeBlock";

class App extends Component {
	setup() {
		this.rootRef = useRef("root");
		this.state = useState(createInitialState());
		this.oldHash = "";
		this.pendingCustomFile = null;
		this.exampleRequestId = 0;

		this.handleHashChange = this.handleHashChange.bind(this);
		this.handleWindowDrag = this.handleWindowDrag.bind(this);
		this.handleWindowDrop = this.handleWindowDrop.bind(this);
		this.handleItemHover = this.handleItemHover.bind(this);
		this.handleItemActivate = this.handleItemActivate.bind(this);
		this.onCustomFileSelected = this.onCustomFileSelected.bind(this);
		this.onCustomContinue = this.onCustomContinue.bind(this);
		this.onCloseCustomModal = this.onCloseCustomModal.bind(this);
		this.onMinify = this.onMinify.bind(this);

		onMounted(() => {
			window.addEventListener("hashchange", this.handleHashChange);
			window.addEventListener("dragenter", this.handleWindowDrag);
			window.addEventListener("dragover", this.handleWindowDrag);
			window.addEventListener("drop", this.handleWindowDrop);
			this.handleHashChange();
		});

		onWillUnmount(() => {
			window.removeEventListener("hashchange", this.handleHashChange);
			window.removeEventListener("dragenter", this.handleWindowDrag);
			window.removeEventListener("dragover", this.handleWindowDrag);
			window.removeEventListener("drop", this.handleWindowDrop);
		});
	}

	get exampleKinds() {
		return exampleKinds;
	}

	hideCustomModal() {
		this.state.custom.visible = false;
		this.state.custom.error = "";
		this.state.custom.hasPendingFile = false;
		this.pendingCustomFile = null;
	}

	resetCustomState() {
		const custom = this.state.custom;
		custom.visible = true;
		custom.step = "step1";
		custom.error = "";
		custom.hasPendingFile = false;
		custom.generatedSource = "";
		custom.sourceMap = null;
		custom.sourcesContent = [];
		custom.sourceFile = "";
		custom.sourceFileIndex = -1;
		this.pendingCustomFile = null;
	}

	handleHashChange() {
		let exampleKind = window.location.hash.replace(/^#/, "");
		if (exampleKind !== "custom-choose") {
			this.hideCustomModal();
		}

		if (exampleKind.indexOf("base64") === 0) {
			const input = exampleKind.split(",").slice(1).map((str) => decodeURIComponent(escape(atob(str))));
			const generatedSource = input.shift();
			const sourceMap = JSON.parse(input.shift());
			this.loadCustomExample(input, generatedSource, sourceMap, "#" + exampleKind);
			this.oldHash = exampleKind;
			return;
		}

		exampleKind = exampleKind.toLowerCase();
		if (exampleKind === "custom") {
			this.state.currentHash = "custom";
			return;
		}
		if (exampleKind === "custom-choose") {
			this.resetCustomState();
			this.state.currentHash = "custom-choose";
			return;
		}

		if (exampleKinds.indexOf(exampleKind) < 0) exampleKind = "typescript";
		this.state.currentHash = exampleKind;
		this.state.pageError = "";
		void this.loadNamedExample(exampleKind);
		this.state.customLink = "";
		this.oldHash = exampleKind;
	}

	loadNamedExample(exampleKind) {
		const requestId = ++this.exampleRequestId;
		return fetchExample(exampleKind)
			.then((example) => {
				if (requestId !== this.exampleRequestId) {
					return;
				}
				const exampleMap = cloneMap(example.map);
				const sources = exampleMap.sourcesContent ? exampleMap.sourcesContent.slice() : [example.original];
				this.loadExample(sources, example.js, exampleMap, {
					hash: exampleKind,
					customLink: "",
				});
			})
			.catch((err) => {
				if (requestId !== this.exampleRequestId) {
					return;
				}
				this.state.pageError = err.message;
			});
	}

	loadExample(sources, generatedSource, sourceMap, options) {
		const mapData = cloneMap(sourceMap);
		mapData.file = mapData.file || "example.js";
		try {
			const map = new SourceMapConsumer(mapData);
			this.state.visualization = generateHtml(map, generatedSource, sources);
			this.state.currentExample = {
				sources: sources.slice(),
				generatedSource,
				sourceMap: mapData,
			};
			this.state.pageError = "";
			this.state.selectedMappingKey = "";
			this.state.currentHash = options.hash || this.state.currentHash;
			this.state.customLink = typeof options.customLink === "string" ? options.customLink : "";
		} catch (err) {
			this.state.pageError = err.message;
			throw err;
		}
	}

	loadCustomExample(sourcesContent, generatedSource, sourceMap, customHash) {
		this.loadExample(sourcesContent, generatedSource, sourceMap, {
			hash: "custom",
			customLink: customHash || buildCustomHash(generatedSource, sourceMap, sourcesContent),
		});
	}

	handleWindowDrag(ev) {
		ev.preventDefault();
		ev.stopPropagation();
		if (this.state.custom.visible) return false;
		this.state.custom.visible = true;
		this.state.custom.step = "drag";
		this.state.custom.error = "";
		this.state.custom.hasPendingFile = false;
		return false;
	}

	handleWindowDrop(ev) {
		ev.preventDefault();
		ev.stopPropagation();

		const files =
			ev.dataTransfer?.files || ev.originalEvent?.dataTransfer?.files || null;
		if (!files || files.length === 0) return false;

		this.state.custom.visible = true;
		this.state.custom.step = "drag";
		this.state.custom.error = "";
		this.state.custom.hasPendingFile = false;

		this.readDroppedFiles(files)
			.then((result) => {
				this.loadCustomExample(result.sourcesContent, result.generatedSource, result.sourceMap);
				this.hideCustomModal();
				this.oldHash = "custom";
				window.location.hash = "custom";
			})
			.catch((err) => {
				this.state.custom.error = err.message;
			});

		return false;
	}

	readDroppedFiles(files) {
		return Promise.all(
			Array.prototype.map.call(files, (file) =>
				readFileAsText(file).then((result) => ({
					file,
					name: file.name,
					result,
				}))
			)
		).then((filesData) => {
			let sourceMapFile;
			let generatedFile;
			const javascriptWithSourceMap = filesData.filter((data) => {
				return (/\.js$/.test(data.name) && SOURCE_MAPPING_URL_REG_EXP.test(data.result)) ||
					(/\.(css|js)$/.test(data.name) && SOURCE_MAPPING_URL_REG_EXP2.test(data.result));
			})[0];

			if (javascriptWithSourceMap) {
				if (typeof atob !== "function") {
					throw new Error("Your browser doesn't support atob. Cannot decode base64.");
				}
				generatedFile = javascriptWithSourceMap;
				filesData.splice(filesData.indexOf(generatedFile), 1);
				const extracted = extractInlineSourceMap(generatedFile.result);
				if (!extracted) {
					throw new Error("Cannot decode embedded SourceMap.");
				}
				generatedFile.result = extracted.generatedSource;
				sourceMapFile = {
					result: JSON.stringify(extracted.sourceMap),
					json: extracted.sourceMap,
				};
			} else {
				const mapFiles = filesData.filter((data) => /\.map$/.test(data.name));
				if (mapFiles.length === 1) {
					sourceMapFile = mapFiles[0];
					filesData.splice(filesData.indexOf(sourceMapFile), 1);
				} else {
					const jsonFiles = filesData.filter((data) => /\.json$/.test(data.name));
					if (jsonFiles.length === 1) {
						sourceMapFile = jsonFiles[0];
						filesData.splice(filesData.indexOf(sourceMapFile), 1);
					} else {
						throw new Error("No SourceMap provided.");
					}
				}
				sourceMapFile.json = JSON.parse(sourceMapFile.result);
				validateSourceMap(sourceMapFile.json);

				const name = sourceMapFile.json.file;
				generatedFile =
					filesData.filter((data) => data.name === name)[0] ||
					filesData.filter((data) => /\.js$/.test(data.name))[0];
				if (!generatedFile) {
					throw new Error("No original file provided.");
				}
				filesData.splice(filesData.indexOf(generatedFile), 1);
			}

			const providedSourcesContent = filesData.map((data) => data.result);
			const sourcesContentSet = sourceMapFile.json.sourcesContent && sourceMapFile.json.sourcesContent.length > 0;
			if (providedSourcesContent.length > 0 && sourcesContentSet) {
				throw new Error("Provided source files, but sourcesContent already provided within SourceMap.");
			}
			return {
				sourcesContent: sourcesContentSet ? sourceMapFile.json.sourcesContent : providedSourcesContent,
				generatedSource: generatedFile.result,
				sourceMap: sourceMapFile.json,
			};
		});
	}

	handleItemHover(mappingKey) {
		this.state.selectedMappingKey = mappingKey;
	}

	handleItemActivate(ev, mappingKey) {
		this.state.selectedMappingKey = mappingKey;
		const root = this.rootRef.el || document.body;
		const items = root.querySelectorAll(`[data-mapping-key="${mappingKey}"]`);
		Array.prototype.forEach.call(items, (elem) => {
			if (elem === ev.currentTarget) return;
			if ("scrollIntoViewIfNeeded" in elem) {
				elem.scrollIntoViewIfNeeded();
				return;
			}
			elem.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "nearest",
			});
		});
	}

	onCustomFileSelected(ev) {
		this.pendingCustomFile = ev.target.files[0] || null;
		this.state.custom.hasPendingFile = !!this.pendingCustomFile;
	}

	onCloseCustomModal() {
		window.location.hash = this.oldHash || "typescript";
	}

	onCustomContinue() {
		if (!this.pendingCustomFile) return;
		const step = this.state.custom.step;
		this.state.custom.error = "";
		readFileAsText(this.pendingCustomFile)
			.then((result) => {
				this.pendingCustomFile = null;
				this.state.custom.hasPendingFile = false;
				if (step === "step1") {
					this.handleGeneratedSource(result);
					return;
				}
				if (step === "step2") {
					this.handleCustomSourceMap(result);
					return;
				}
				if (step === "step3") {
					this.handleCustomSource(result);
				}
			})
			.catch((err) => {
				this.state.custom.error = err.message;
			});
	}

	handleGeneratedSource(generatedSource) {
		const custom = this.state.custom;
		custom.generatedSource = generatedSource;
		const extracted = extractInlineSourceMap(generatedSource);
		if (extracted) {
			custom.generatedSource = extracted.generatedSource;
			custom.sourceMap = extracted.sourceMap;
			custom.sourcesContent = [];
			this.advanceCustomStep();
			return;
		}
		custom.step = "step2";
	}

	handleCustomSourceMap(sourceMapText) {
		try {
			const sourceMap = JSON.parse(sourceMapText);
			validateSourceMap(sourceMap);
			this.state.custom.sourceMap = sourceMap;
			this.state.custom.sourcesContent = [];
			this.advanceCustomStep();
		} catch (err) {
			this.state.custom.error = err.message;
		}
	}

	handleCustomSource(originalSource) {
		this.state.custom.sourcesContent[this.state.custom.sourceFileIndex] = originalSource;
		this.advanceCustomStep();
	}

	advanceCustomStep() {
		const custom = this.state.custom;
		if (!custom.sourceMap || !custom.sourceMap.sources || !custom.sourceMap.mappings) {
			custom.error = "This is not a valid SourceMap.";
			return;
		}
		if (custom.sourceMap.sourcesContent) {
			custom.sourcesContent = custom.sourceMap.sourcesContent.slice();
			this.finishCustomFlow();
			return;
		}
		for (let i = 0; i < custom.sourceMap.sources.length; i++) {
			if (!custom.sourcesContent[i]) {
				custom.sourceFile = custom.sourceMap.sources[i];
				custom.sourceFileIndex = i;
				custom.step = "step3";
				return;
			}
		}
		this.finishCustomFlow();
	}

	finishCustomFlow() {
		try {
			this.loadCustomExample(this.state.custom.sourcesContent, this.state.custom.generatedSource, this.state.custom.sourceMap);
			this.hideCustomModal();
			this.oldHash = "custom";
			window.location.hash = "custom";
		} catch (err) {
			this.state.custom.error = err.message;
			if (err.stack) {
				console.error(err.stack);
			}
		}
	}

	onMinify() {
		const currentExample = this.state.currentExample;
		if (!currentExample) return;
		try {
			const result = UglifyJS.minify(currentExample.generatedSource, {
				outSourceMap: "example.map",
				output: {
					beautify: true,
				},
			});
			let minmap = JSON.parse(result.map);
			minmap.file = "example";
			minmap = new SourceMapConsumer(result.map);
			minmap = SourceMapGenerator.fromSourceMap(minmap);
			minmap.setSourceContent("?", currentExample.generatedSource);
			currentExample.sourceMap.sourcesContent = currentExample.sources;
			minmap.applySourceMap(new SourceMapConsumer(currentExample.sourceMap), "?");
			minmap = minmap.toJSON();
			this.loadCustomExample(minmap.sourcesContent, result.code, minmap);
			this.oldHash = "custom";
			window.location.hash = "custom";
		} catch (err) {
			this.state.pageError = err.message;
		}
	}
}

App.components = {
	CodeBlock,
};

App.template = "source-map-visualization.App";

export default App;
